import cx from "classnames";
import React, { FC, HTMLAttributes, ReactNode, useCallback } from "react";
import { PropsWithPropStyling } from "../../shared/propStyling";
import { ToggleGroup, ToggleGroupProps } from "../ToggleGroup";
import { ToggleGroupChangeHandler } from "../ToggleGroup/ToggleGroupRoot";

interface DefaultOptionType {
  label: string;
  value: string;
}

export interface RadioGroupOptionLabelGetter<OptionType> {
  (option: OptionType): ReactNode;
}

export interface RadioGroupOptionValueGetter<OptionType> {
  (option: OptionType): string | number;
}

export interface RadioGroupChangeHandler<OptionType> {
  (option: OptionType | null): any;
}

export interface RadioGroupCoreProps<OptionType = unknown> {
  disabled?: boolean;
  getOptionLabel?: RadioGroupOptionLabelGetter<OptionType>;
  getOptionValue?: RadioGroupOptionValueGetter<OptionType>;
  name?: string;
  onChange?: RadioGroupChangeHandler<OptionType>;
  options: OptionType[];
  value?: OptionType | null;
  variant: "horizontal";
}

export type RadioGroupProps<OptionType = unknown> = PropsWithPropStyling<
  Omit<HTMLAttributes<HTMLElement>, "onChange"> &
    Omit<ToggleGroupProps.Root, "onChange" | "value"> &
    RadioGroupCoreProps<OptionType>
>;

const defaultGetOptionLabel: RadioGroupOptionLabelGetter<DefaultOptionType> = (
  option
) => option.label;

const defaultGetOptionValue: RadioGroupOptionValueGetter<DefaultOptionType> = (
  option
) => option.value;

interface RadioGroup<OptionType = DefaultOptionType>
  extends FC<RadioGroupProps<OptionType>> {}

/**
 * @deprecated Use ToggleGroup instead.
 */
export const RadioGroup = <OptionType extends unknown = DefaultOptionType>({
  className,
  disabled = false,
  getOptionLabel = defaultGetOptionLabel as any,
  getOptionValue = defaultGetOptionValue as any,
  name,
  onChange,
  options,
  value,
  variant,
  ...rest
}: RadioGroupProps<OptionType>) => {
  const handleChange = useCallback<ToggleGroupChangeHandler>(
    (value) => {
      const selected =
        options.find((option) => value === getOptionValue(option).toString()) ??
        null;
      if (onChange) onChange(selected);
    },
    [getOptionValue, onChange, options]
  );
  return (
    <ToggleGroup.Root
      className={cx("TombacRadioGroup", className)}
      disabled={disabled}
      groupContainerProps={{ $width: "auto" }}
      name={name}
      onChange={handleChange}
      value={value ? getOptionValue(value).toString() : undefined}
      {...rest}
    >
      {options.map((option) => {
        const label = getOptionLabel(option);
        const value = getOptionValue(option);
        return (
          <ToggleGroup.Item key={value} value={value.toString()}>
            {label}
          </ToggleGroup.Item>
        );
      })}
    </ToggleGroup.Root>
  );
};
